import React, { useState } from "react";
import { useForm, FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "api/apiInstance";
import { useFormPrepopulation, buildAfricaFieldMappings } from "hooks/useFormPrepopulation";

export default function BuildAfricaForm({ productID }) {
  const [loading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [crcConsentChecked, setCrcConsentChecked] = useState(false);
  const customization = JSON.parse(localStorage.getItem("oze-loans") || "");
  const logo = customization.state.customization.logoUrl;
  const [searchParams] = useSearchParams();

  const MAX_UPLOAD_FILE_SIZE = 5 * 1024 * 1024;
  const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg"];
  const ACCEPTED_BANK_STATEMENT_TYPES = [
    "image/jpeg",
    "image/png",
    "application/pdf",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm();

  const getErrorMessage = (
      error: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined
  ) => {
    if (!error) return null;
    return typeof error.message === "string"
        ? error.message
        : "This field is required";
  };

  // Prepopulate form from URL query parameters using declarative configuration
  useFormPrepopulation(searchParams, setValue, buildAfricaFieldMappings);

  const onSubmit = async (data: any) => {
    setLoading(true);
    const formData = new FormData();

    const personalInformationDto = JSON.stringify({
      fullName: data.fullName,
      dob: data.dob,
      email: data.email,
      phone: data.phone,
      maritalStatus: data.maritalStatus,
      gender: data.gender,
      address: data.address,
      nextOfKin: data.nextOfKin,
      nextOfKinPhone: data.nextOfKinPhone,
    });

    const businessInformationDto = JSON.stringify({
      businessName: data.businessName,
      businessNature: data.businessNature,
      businessAddress: data.businessAddress,
      buildingAddress: data.buildingAddress,
      cacNumber: data.cacNumber,
      tin: data.tin,
      tinType: data.tinType,
    });

    const financialInformationDto = JSON.stringify({
      bvn: data.bvn,
    });

    const documentsDto = JSON.stringify({
      bankStatement: data.bankStatement[0],
      idDocument: data.idDocument[0],
      cacDocument: data.cacDocument[0],
    });

    formData.append("personalInformationDto", personalInformationDto);
    formData.append("businessInformationDto", businessInformationDto);
    formData.append("documentsDto", documentsDto);
    formData.append("bvn", data.bvn);
    formData.append("loanAmount", data.loanAmount);
    formData.append("financialInformationDto", financialInformationDto);
    formData.append("acquisitionPartnerProductId", productID);
    formData.append("bankStatement", data.bankStatement[0]);
    formData.append("idDocument", data.idDocument[0]);
    formData.append("cacDocument", data.cacDocument[0]);
    formData.append("loanRepaymentPeriod", data.loanRepaymentPeriod);
    formData.append("consent", String(crcConsentChecked));

    try {
      toast.loading("Please wait, Application is in Progress...", {
        position: "top-center",
      });
      await axiosInstance.post("/build-africa", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss();
      toast.success("Application was submitted successfully", {
        position: "top-center",
      });
      reset();
    } catch (error: any) {
      toast.error(error.response.data.message || "An error occurred");
      setTimeout(() => {
        toast.dismiss();
      }, 4000);
    } finally {
      setLoading(false);
    }
  };

  return (
      <div
          className={`min-h-screen flex items-center justify-center bg-gray-100 px-4 py-10 ${
              loading ? "pointer-events-none opacity-50" : ""
          }`}
      >
        <div className="bg-white max-w-6xl w-full p-10 md:p-12 rounded-lg shadow-lg relative">
          {" "}
          {/* Added relative for overlay positioning */}
          {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
                <div className="loader">Loading...</div>{" "}
                {/* Replaceaa this with a spinner component if desired */}
              </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="flex justify-start">
              <img src={logo} alt="Build Left" className="h-20 mb-4" />
            </div>

            <div className="flex justify-end">
              <img
                  src="buildafrica.png"
                  alt="Build Right"
                  className="h-20 mb-4"
              />
            </div>
          </div>
          <div className="text-center mb-10">
            <h4 className="text-xl md:text-2xl font-semibold center">
              Welcome to Build Africa Credit Scheme <br></br>
              Powered by Oze
            </h4>
            <p className="text-gray-500 text-sm">
              Complete the form below to apply for your financing
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <h3 className="text-red-600 font-semibold mb-4">
              1. Personal Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your Full name?
                </label>
                <input
                    {...register("fullName", {
                      required: "Full Name is required",
                      validate: (value) => {
                        const fullNameParts = value.trim().split(" ");
                        return (
                            (fullNameParts.length >= 2 &&
                                fullNameParts.every((part) => part.length > 0)) ||
                            "Please enter your full name (e.g. John Doe Maxwell)"
                        );
                      },
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter your full name (e.g. John Doe Maxwell)"
                />
                {errors.fullName && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.fullName)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  Date of Birth
                </label>
                <input
                    type="date"
                    {...register("dob", {
                      required: "Date of Birth is required",
                      validate: (value) => {
                        const dob = new Date(value);
                        const today = new Date();
                        if (dob > today) {
                          return "Date of Birth cannot be in the future";
                        }
                        const ageDiff = today.getFullYear() - dob.getFullYear();
                        const hasBirthdayPassed =
                            today.getMonth() > dob.getMonth() ||
                            (today.getMonth() === dob.getMonth() &&
                                today.getDate() >= dob.getDate());
                        const age = hasBirthdayPassed ? ageDiff : ageDiff - 1;
                        if (age < 18) {
                          return "You must be at least 18 years old";
                        }
                        return true;
                      },
                    })}
                    className="border rounded p-2 w-full"
                />
                {errors.dob && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.dob)}
                    </p>
                )}
              </div>
              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your Email address?
                </label>
                <input
                    type="email"
                    {...register("email", {
                      required: "Email Address is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="e.g buildafrica@gmail.com"
                />
                {errors.email && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.email)}
                    </p>
                )}
              </div>
              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your Phone number?
                </label>
                <input
                    {...register("phone", {
                      required: "Phone Number is required",
                      pattern: {
                        value: /^234\d{10}$/,
                        message:
                            "Phone number must start with 234 followed by 10 digits",
                      },
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="e.g 2348000000000"
                />
                {errors.phone && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.phone)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  Marital status
                </label>
                <select
                    {...register("maritalStatus", {
                      required: "Marital Status is required",
                    })}
                    className="border rounded p-2 w-full"
                >
                  <option value="">Select an option</option>
                  <option>Single</option>
                  <option>Married</option>
                </select>
                {errors.maritalStatus && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.maritalStatus)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">Gender</label>
                <select
                    {...register("gender", { required: "Gender is required" })}
                    className="border rounded p-2 w-full"
                >
                  <option value="">Select an option</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
                {errors.gender && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.gender)}
                    </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm mb-1 font-medium">
                  What is your Residential address?
                </label>
                <input
                    {...register("address", {
                      required: "Residential Address is required",
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter your residential address"
                />
                {errors.address && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.address)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  Name of Next of kin?
                </label>
                <input
                    {...register("nextOfKin", {
                      required: "Next of Kin is required",
                    })}
                    className="border rounded p-2 w-full"
                />
                {errors.nextOfKin && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.nextOfKin)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  Next of kin phone number
                </label>
                <input
                    {...register("nextOfKinPhone", {
                      required: "Next of Kin Phone Number is required",
                      pattern: {
                        value: /^234\d{10}$/,
                        message:
                            "Phone number must start with 234 followed by 10 digits",
                      },
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="e.g 2348000000000"
                />
                {errors.nextOfKinPhone && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.nextOfKinPhone)}
                    </p>
                )}
              </div>
            </div>

            <h3 className="text-red-600 font-semibold mt-10 mb-4">
              2. Business Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your business name?
                </label>
                <input
                    {...register("businessName", {
                      required: "Business Name is required",
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter your business name"
                />
                {errors.businessName && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.businessName)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  What does your business do ?
                </label>
                <input
                    {...register("businessNature", {
                      required: "Business Description is required",
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter the nature of your business"
                />
                {errors.businessNature && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.businessNature)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your business address ?
                </label>
                <input
                    {...register("businessAddress")}
                    className="border rounded p-2 w-full"
                    placeholder="Business address(if applicable)"
                />
                {errors.businessAddress && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.businessAddress)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is the building site address (Site at which building is
                  taking place)
                </label>
                <input
                    {...register("buildingAddress")}
                    className="border rounded p-2 w-full"
                    placeholder="Bulding site address"
                />
                {errors.buildingAddress && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.buildingAddress)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your business CAC number
                </label>
                <input
                    {...register("cacNumber", {
                      required: false,
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter business CAC number"
                />
                {errors.cacNumber && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.cacNumber)}
                    </p>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1 font-medium">
                  What is your TIN? (Business TIN: if none, enter your personal
                  TIN)
                </label>
                <input
                    {...register("tin", { required: "TIN is required" })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter your TIN"
                />
                {errors.tin && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.tin)}
                    </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm mb-1 font-medium">TIN type</label>
                <select
                    {...register("tinType", { required: "TIN Type is required" })}
                    className="border rounded p-2 w-full"
                >
                  <option value="">Select an option</option>
                  <option value="NONE">None</option>
                  <option value="BUSINESS">Business</option>
                  <option value="PERSONAL">Personal</option>
                  <option value="PERSONALANDBUSINESS">
                    Personal and business
                  </option>
                </select>
                {errors.tinType && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.tinType)}
                    </p>
                )}
              </div>
            </div>

            <h3 className="text-red-600 font-semibold mt-10 mb-4">
              3. Financial Details
            </h3>

            <div>
              <label className="block text-sm mb-1 font-medium">
                What is your bank verification number (BVN) ?
              </label>
              <input
                  type="number"
                  {...register("bvn", {
                    required: "BVN is required",
                    pattern: {
                      value: /^\d{11}$/,
                      message: "BVN must be exactly 11 digits",
                    },
                  })}
                  className="border rounded p-2 w-full"
                  placeholder="Enter your BVN"
              />
              {errors.bvn && (
                  <p className="text-red-500 text-sm">
                    {getErrorMessage(errors.bvn)}
                  </p>
              )}
            </div>

            <h3 className="text-red-600 font-semibold mt-10 mb-4">
              4. Loan Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm mb-1 font-medium">
                  How much do you need to borrow ?
                </label>
                <input
                    {...register("loanAmount", {
                      required: "Loan Amount is required",
                      pattern: { value: /^[0-9]+$/, message: "Only numbers are allowed" },
                      validate: value => value > 0 || "Loan amount must be greater than 0"
                    })}
                    className="border rounded p-2 w-full"
                    placeholder="Enter an amount" onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }} />
                {errors.loanAmount && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.loanAmount)}
                    </p>
                )}
              </div>

              <div>
                <label className="text-sm mb-1 font-medium">
                  After how long would you like to repay the loan?
                </label>
                <select
                    {...register("loanRepaymentPeriod", {
                      required: "Repayment period is required",
                    })}
                    className="border rounded p-2 w-full"
                >
                  <option value="">Select repayment period</option>
                  <option value="30">30 days</option>
                  <option value="45">45 days</option>
                </select>
                {errors.repaymentDays && (
                    <p className="text-red-500 text-sm">
                      {getErrorMessage(errors.repaymentDays)}
                    </p>
                )}
              </div>
            </div>
            <h3 className="text-red-600 font-semibold mt-10 mb-2">
              5. Document Upload
            </h3>
            <p className="text-xs text-gray-700 mb-4">
              Please ensure every detail of your Bank Statement (latest 3 months),
              ID document and CAC document (if available) is clearly visible.{" "}
              <br />
              For IDs, we accept only:{" "}
              <span className="text-red-500 font-medium">
              National IDs, Driving Licenses, Passport. Also, Front & Back of
              the ID should be uploaded.
            </span>
              <span className="text-red-500 font-medium">
              {" "}
                Each file must be less than 5MB
            </span>
            </p>
            <div>
              <label className="block text-sm mb-1 font-medium">
                Bank statement (3 months) <br />
                <span className="text-yellow-500 font-medium">
                Supported formats: JPEG, JPG, PNG, XLS, XLSX, PDF
              </span>
              </label>
              <input
                  type="file"
                  {...register("bankStatement", {
                    required: "Bank Statement is required",
                    validate: (files: FileList) => {
                      const file = files?.[0];
                      if (!file) return "Bank Statement is required";
                      if (!ACCEPTED_BANK_STATEMENT_TYPES.includes(file.type)) {
                        return "Only JPEG, PNG, PDF, XLS, XLSX files are allowed";
                      }
                      if (file.size > MAX_UPLOAD_FILE_SIZE) {
                        return "File size must be less than 5MB";
                      }
                      return true;
                    },
                  })}
                  className="border rounded p-2 w-full"
              />
              {errors.bankStatement && (
                  <p className="text-red-500 text-sm">
                    {getErrorMessage(errors.bankStatement)}
                  </p>
              )}
            </div>

            <div>
              <label className="block text-sm mb-1 font-medium">
                ID document <br />
                <span className="text-yellow-500 font-medium">
                Supported formats: JPEG, JPG, PNG
              </span>
              </label>
              <input
                  type="file"
                  {...register("idDocument", {
                    required: "ID Document is required",
                    validate: (files: FileList) => {
                      const file = files?.[0];
                      if (!file) return "ID Document is required";
                      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                        return "Only JPEG and PNG files are allowed";
                      }
                      if (file.size > MAX_UPLOAD_FILE_SIZE) {
                        return "File size must be less than 5MB";
                      }
                      return true;
                    },
                  })}
                  className="border rounded p-2 w-full"
              />
              {errors.idDocument && (
                  <p className="text-red-500 text-sm">
                    {getErrorMessage(errors.idDocument)}
                  </p>
              )}
            </div>

            <div>
              <label className="block text-sm mb-1 font-medium">
                CAC document (Optional) <br />
                <span className="text-yellow-500 font-medium">
                Supported formats: JPEG, JPG, PNG
              </span>
              </label>
              <input
                  type="file"
                  {...register("cacDocument", {
                    validate: (files: FileList) => {
                      const file = files?.[0];
                      if (!file) return true;
                      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                        return "Only JPEG, PNG, JPG files are allowed";
                      }
                      if (file.size > MAX_UPLOAD_FILE_SIZE) {
                        return "File size must be less than 5MB";
                      }
                      return true;
                    },
                  })}
                  className="border rounded p-2 w-full"
              />
              {errors.cacDocument && (
                  <p className="text-red-500 text-sm">
                    {getErrorMessage(errors.cacDocument)}
                  </p>
              )}
            </div>

            {/* Consent Notice */}
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  Build Africa Company Limited has partnered with Oze Nigeria Ltd (&ldquo;Oze&rdquo;) to make access to financing easier for its merchants. By submitting this application, you consent to Oze using and sharing your personal and business information with its lending partners and service providers for loan assessment and processing, in accordance with Oze&apos;s{" "}
                  <a
                    href="https://getoze.com/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://getoze.com/terms-of-service/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    Terms of Service
                  </a>.
                </span>
              </label>
            </div>

            {/* Credit Bureau Consent */}
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={crcConsentChecked}
                  onChange={(e) => setCrcConsentChecked(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  I authorize the lender to obtain my credit report from licensed Nigerian credit bureaus for the purpose of evaluating my loan application. I understand this may involve sharing my BVN and personal details with these bureaus. I consent to this use and to the lender securely storing a record of my consent.
                </span>
              </label>
            </div>

            <button
                type="submit"
                className={`bg-blue-600 text-white w-full py-3 mt-8 rounded hover:bg-blue-700 ${
                    loading || !consentChecked || !crcConsentChecked ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading || !consentChecked || !crcConsentChecked}
            >
              Submit Application
            </button>
          </form>

          <div className="flex flex-col items-center justify-center mt-6">
            <p className="mb-2">Powered by</p>
            <img
                src="https://oze-static-assets.s3.amazonaws.com/lms/oze/oze-logo.png"
                alt="Oze"
                className="h-10"
            />
          </div>
        </div>
      </div>
  );
}
